import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Search, ClipboardCheck, Camera, FileText, ShieldCheck, CheckCircle2, Phone } from 'lucide-react';
import { useLanguage } from '../components/LanguageContext';
const fadeIn = {
  initial: {
    opacity: 0,
    y: 20
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true
  }
};
export function FreeInspectionPage() {
  const {
    t
  } = useLanguage();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <div className="pt-20" data-id="element-404">
      {/* Hero Banner */}
      <section className="relative bg-brand-dark py-24 overflow-hidden" data-id="element-405">
        <div className="absolute inset-0 opacity-20 bg-cover bg-center" style={{
        backgroundImage: 'url("https://images.unsplash.com/photo-1504307651254-35680f356dfd?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80")'
      }} data-id="element-406" />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-dark via-brand-dark/95 to-brand-dark/80" data-id="element-407" />

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-408">
          <Link to="/" className="inline-flex items-center gap-2 text-brand-gold hover:text-white transition-colors mb-8 font-semibold" data-id="element-409">
            <ArrowLeft size={20} data-id="element-410" />
            {t.free.back}
          </Link>

          <motion.div {...fadeIn} data-id="element-411">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-red/10 border border-brand-red/20 mb-6" data-id="element-412">
              <Search className="text-brand-gold" size={20} data-id="element-413" />
              <span className="text-brand-gold font-semibold text-sm uppercase tracking-wider" data-id="element-414">
                {t.free.badge}
              </span>
            </div>
            <h1 className="text-4xl md:text-6xl font-black text-white mb-6" data-id="element-415">
              {t.free.title}
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl leading-relaxed" data-id="element-416">
              {t.free.desc}
            </p>
          </motion.div>
        </div>
      </section>

      {/* What Is a Public Adjuster Inspection */}
      <section className="py-20 bg-white" data-id="element-417">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-418">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-419">
            <motion.div {...fadeIn} data-id="element-420">
              <h2 className="text-brand-red font-bold tracking-wider uppercase mb-2" data-id="element-421">
                {t.free.uTag}
              </h2>
              <h3 className="text-3xl md:text-4xl font-black text-brand-dark mb-6" data-id="element-422">
                {t.free.uTitle}
              </h3>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed" dangerouslySetInnerHTML={{
              __html: t.free.uP1
            }} data-id="element-423" />
              <p className="text-lg text-gray-600 leading-relaxed" data-id="element-424">
                {t.free.uP2}
              </p>
            </motion.div>

            <motion.div {...fadeIn} transition={{
            delay: 0.2
          }} className="relative" data-id="element-425">
              <img src="https://images.unsplash.com/photo-1581578731548-c64695cc6952?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80" alt="Professional inspector examining property damage" className="rounded-sm shadow-2xl w-full aspect-[4/3] object-cover" data-id="element-426" />
              <div className="absolute -bottom-6 -left-6 bg-brand-gold text-brand-dark p-5 rounded-sm shadow-xl hidden md:block" data-id="element-427">
                <div className="text-3xl font-black" data-id="element-428">100%</div>
                <div className="font-bold text-sm uppercase tracking-wider whitespace-pre-line" data-id="element-429">
                  {t.free.freeBadge}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why It Matters */}
      <section className="py-20 bg-brand-gray" data-id="element-430">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-431">
          <motion.div {...fadeIn} className="text-center mb-16" data-id="element-432">
            <h2 className="text-brand-red font-bold tracking-wider uppercase mb-2" data-id="element-433">
              {t.free.wTag}
            </h2>
            <h3 className="text-3xl md:text-4xl font-black text-brand-dark mb-6" data-id="element-434">
              {t.free.wTitle}
            </h3>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto" data-id="element-435">
              {t.free.wDesc}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-436">
            {[{
            title: t.free.w1Title,
            description: t.free.w1Desc,
            icon: Search
          }, {
            title: t.free.w2Title,
            description: t.free.w2Desc,
            icon: Camera
          }, {
            title: t.free.w3Title,
            description: t.free.w3Desc,
            icon: ClipboardCheck
          }].map((item, index) => <motion.div key={index} {...fadeIn} transition={{
            delay: index * 0.1
          }} className="bg-white p-8 rounded-sm shadow-lg border-t-4 border-brand-red" data-id="element-437">
                <div className="w-14 h-14 bg-brand-red/10 rounded-full flex items-center justify-center mb-6" data-id="element-438">
                  <item.icon className="text-brand-red" size={28} data-id="element-439" />
                </div>
                <h4 className="text-xl font-bold text-brand-dark mb-3" data-id="element-440">
                  {item.title}
                </h4>
                <p className="text-gray-600 leading-relaxed" data-id="element-441">
                  {item.description}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* What We Inspect */}
      <section className="py-20 bg-white" data-id="element-442">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-443">
          <motion.div {...fadeIn} className="mb-12" data-id="element-444">
            <h2 className="text-brand-red font-bold tracking-wider uppercase mb-2" data-id="element-445">
              {t.free.cTag}
            </h2>
            <h3 className="text-3xl md:text-4xl font-black text-brand-dark mb-6" data-id="element-446">
              {t.free.cTitle}
            </h3>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-447">
            {t.free.cItems.map((item, index) => <motion.div key={index} {...fadeIn} transition={{
            delay: index * 0.05
          }} className="flex items-start gap-3 p-4 bg-brand-gray rounded-sm" data-id="element-448">
                <CheckCircle2 className="text-brand-red shrink-0 mt-0.5" size={22} data-id="element-449" />
                <span className="text-gray-800 font-medium text-lg" data-id="element-450">
                  {item}
                </span>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Insurance Company vs Elite */}
      <section className="py-20 bg-brand-dark text-white" data-id="element-451">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-452">
          <motion.div {...fadeIn} className="text-center mb-16" data-id="element-453">
            <h2 className="text-brand-gold font-bold tracking-wider uppercase mb-2" data-id="element-454">
              {t.free.dTag}
            </h2>
            <h3 className="text-3xl md:text-4xl font-black mb-6" data-id="element-455">
              {t.free.dTitle}
            </h3>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-456">
            <motion.div {...fadeIn} className="bg-white/5 border border-white/10 p-8 rounded-sm" data-id="element-457">
              <h4 className="text-xl font-bold text-gray-400 mb-6 uppercase tracking-wider" data-id="element-458">
                {t.free.dTheirTitle}
              </h4>
              <ul className="space-y-4" data-id="element-459">
                {t.free.dTheirItems.map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-460">
                    <span className="text-red-400 font-bold text-lg mt-0.5" data-id="element-461">
                      ✕
                    </span>
                    <span className="text-gray-400" data-id="element-462">{item}</span>
                  </li>)}
              </ul>
            </motion.div>

            <motion.div {...fadeIn} transition={{
            delay: 0.15
          }} className="bg-brand-red/10 border-2 border-brand-red p-8 rounded-sm" data-id="element-463">
              <h4 className="text-xl font-bold text-brand-gold mb-6 uppercase tracking-wider" data-id="element-464">
                {t.free.dEliteTitle}
              </h4>
              <ul className="space-y-4" data-id="element-465">
                {t.free.dEliteItems.map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-466">
                    <CheckCircle2 className="text-brand-gold shrink-0 mt-0.5" size={20} data-id="element-467" />
                    <span className="text-white" data-id="element-468">{item}</span>
                  </li>)}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What Happens After */}
      <section className="py-20 bg-white" data-id="element-469">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-470">
          <motion.div {...fadeIn} className="mb-12" data-id="element-471">
            <h2 className="text-brand-red font-bold tracking-wider uppercase mb-2" data-id="element-472">
              {t.free.aTag}
            </h2>
            <h3 className="text-3xl md:text-4xl font-black text-brand-dark mb-6" data-id="element-473">
              {t.free.aTitle}
            </h3>
          </motion.div>

          <div className="space-y-8" data-id="element-474">
            {[{
            step: '01',
            title: t.free.a1Title,
            description: t.free.a1Desc,
            icon: FileText
          }, {
            step: '02',
            title: t.free.a2Title,
            description: t.free.a2Desc,
            icon: ClipboardCheck
          }, {
            step: '03',
            title: t.free.a3Title,
            description: t.free.a3Desc,
            icon: ShieldCheck
          }].map((item, index) => <motion.div key={index} {...fadeIn} transition={{
            delay: index * 0.1
          }} className="flex flex-col md:flex-row gap-6 items-start p-8 bg-brand-gray rounded-sm" data-id="element-475">
                <div className="flex items-center gap-4 shrink-0" data-id="element-476">
                  <span className="text-5xl font-black text-brand-red/20" data-id="element-477">
                    {item.step}
                  </span>
                  <div className="w-14 h-14 bg-brand-red/10 rounded-full flex items-center justify-center" data-id="element-478">
                    <item.icon className="text-brand-red" size={28} data-id="element-479" />
                  </div>
                </div>
                <div data-id="element-480">
                  <h4 className="text-2xl font-bold text-brand-dark mb-2" data-id="element-481">
                    {item.title}
                  </h4>
                  <p className="text-gray-600 leading-relaxed text-lg" data-id="element-482">
                    {item.description}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-brand-red" data-id="element-483">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-484">
          <motion.div {...fadeIn} data-id="element-485">
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6" data-id="element-486">
              {t.free.ctaTitle}
            </h2>
            <p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto" data-id="element-487">
              {t.free.ctaDesc}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-488">
              <a href="tel:3054170530" className="inline-flex items-center justify-center gap-2 bg-white text-brand-red hover:bg-brand-gold hover:text-brand-dark px-8 py-4 rounded-sm font-bold text-lg transition-colors" data-id="element-489">
                <Phone size={22} data-id="element-490" />
                {t.nav.call} (305) 417-0530
              </a>
              <a href="tel:3057857829" className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white text-white hover:bg-white hover:text-brand-red px-8 py-4 rounded-sm font-bold text-lg transition-colors" data-id="element-491">
                <Phone size={22} data-id="element-492" />
                {t.nav.call} (305) 785-7829
              </a>
            </div>
            <div className="mt-8" data-id="element-493">
              <Link to="/" className="inline-flex items-center gap-2 text-white/80 hover:text-white font-semibold transition-colors" data-id="element-494">
                <ArrowLeft size={18} data-id="element-495" />
                {t.free.back}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
}